/**
 * 多边动态筛选项
 * 与管理端代码表保持一致：
 * - mld_tag      标签/资讯类型
 * - mld_category 动态分类
 * - mld_org      多边组织
 * - mld_region   区域
 */

export const MD_JOIN = '，'

export const MD_DICT = {
  tag: 'mld_tag',
  category: 'mld_category',
  region: 'mld_region',
  org: 'mld_org'
}

/** 接口不可用时的兜底选项（item_value 须与代码表一致） */
export const MD_TAG_OPTIONS = [
  { code: 'opportunity', title: '商机', titleEn: 'Opportunity' },
  { code: 'risk', title: '风险', titleEn: 'Risk' },
  { code: 'neutral', title: '中性', titleEn: 'Neutral' }
]

export const MD_CATEGORY_OPTIONS = [
  { code: 'economic', title: '经济预测', titleEn: 'Economic Forecast' },
  { code: 'rule', title: '规则变动', titleEn: 'Rule Change' },
  { code: 'general', title: '综合动态', titleEn: 'General Dynamics' }
]

export const MD_REGION_OPTIONS = [
  { code: 'global', title: '全球', titleEn: 'Global' },
  { code: 'europe', title: '欧洲', titleEn: 'Europe' },
  { code: 'asia', title: '亚洲', titleEn: 'Asia' },
  { code: 'africa', title: '非洲', titleEn: 'Africa' },
  { code: 'america', title: '美洲', titleEn: 'Americas' },
  { code: 'oceania', title: '大洋洲', titleEn: 'Oceania' },
  { code: 'cross', title: '跨洲', titleEn: 'Transcontinental' }
]

export const MD_ORG_OPTIONS = [
  { code: 'eu', title: '欧盟', titleEn: 'EU' },
  { code: 'opec', title: '欧佩克', titleEn: 'OPEC' },
  { code: 'eurozone', title: '欧元区', titleEn: 'Eurozone' },
  { code: 'iea', title: '国际能源署', titleEn: 'IEA' },
  { code: 'imf', title: '国际货币基金组织', titleEn: 'IMF' },
  { code: 'ecb', title: '欧洲央行', titleEn: 'ECB' },
  { code: 'wto', title: '世界贸易组织', titleEn: 'WTO' },
  { code: 'wb', title: '世界银行', titleEn: 'World Bank' },
  { code: 'adb', title: '亚洲开发银行', titleEn: 'ADB' },
  { code: 'fao', title: '联合国粮农组织', titleEn: 'FAO' },
  { code: 'ec', title: '欧盟委员会', titleEn: 'European Commission' },
  { code: 'un', title: '联合国', titleEn: 'UN' },
  { code: 'eec', title: '欧亚经济委员会', titleEn: 'EEC' },
  { code: 'opec_plus', title: '欧佩克+', titleEn: 'OPEC+' },
  { code: 'ecowas', title: '西非国家经济共同体', titleEn: 'ECOWAS' },
  { code: 'wsts', title: '世界半导体贸易统计组织', titleEn: 'WSTS' },
  { code: 'unctad', title: '联合国贸易和发展会议', titleEn: 'UNCTAD' },
  { code: 'bis', title: '国际清算银行', titleEn: 'BIS' },
  { code: 'eib', title: '欧洲投资银行', titleEn: 'EIB' },
  { code: 'iata', title: '国际航空运输协会', titleEn: 'IATA' },
  { code: 'wef', title: '世界经济论坛', titleEn: 'WEF' },
  { code: 'wfp', title: '世界粮食计划署', titleEn: 'WFP' },
  { code: 'afdb', title: '非洲开发银行', titleEn: 'AfDB' },
  { code: 'ep', title: '欧洲议会', titleEn: 'European Parliament' },
  { code: 'unece', title: '联合国欧洲经济委员会', titleEn: 'UNECE' },
  { code: 'amore', title: '东盟与中日韩宏观经济研究办公室', titleEn: 'AMRO' },
  { code: 'bofa', title: '博鳌亚洲论坛', titleEn: 'Boao Forum for Asia' }
]

export const MD_FALLBACK = {
  [MD_DICT.tag]: MD_TAG_OPTIONS,
  [MD_DICT.category]: MD_CATEGORY_OPTIONS,
  [MD_DICT.region]: MD_REGION_OPTIONS,
  [MD_DICT.org]: MD_ORG_OPTIONS
}

/**
 * 将代码表项映射为前端选项
 * value -> code；中文展示 textcn，英文展示 texten
 * 接口 title 为「中性(Neutral)」混合文案，不用于页面展示
 */
export function mapDictItem (item) {
  if (!item) return null
  const code = item.value != null ? String(item.value) : (item.code || item.itemValue || '')
  if (!code) return null
  const textcn = item.textcn || item.text || ''
  const texten = item.texten || item.textEn || ''
  return {
    code,
    textcn,
    texten,
    title: textcn,
    titleEn: texten
  }
}

export function mapDictItems (list) {
  if (!Array.isArray(list)) return []
  return list.map(mapDictItem).filter(Boolean)
}

export function optionLabel (item, isEN) {
  if (!item) return ''
  if (isEN) return item.texten || item.titleEn || item.textcn || item.title || item.code || ''
  return item.textcn || item.title || item.texten || item.titleEn || item.code || ''
}

export function findOptionLabel (options, code, isEN) {
  const item = (options || []).find(o => o.code === code)
  return item ? optionLabel(item, isEN) : code
}

export function joinCodes (list) {
  if (!list || !list.length) return ''
  return list.map(item => {
    if (typeof item === 'string') {
      try {
        return JSON.parse(item).code || JSON.parse(item).id
      } catch (e) {
        return item
      }
    }
    return item.code || item.id
  }).filter(Boolean).join(MD_JOIN)
}

export function normalizeRegionStatistic (result, isEN, regionOptions = MD_REGION_OPTIONS) {
  const base = (regionOptions || MD_REGION_OPTIONS).map(r => ({
    code: r.code,
    title: optionLabel(r, isEN),
    count: 0
  }))
  if (!result) return base

  const mapCount = (code, count) => {
    const row = base.find(b => b.code === code || b.title === code)
    if (row) row.count = Number(count) || 0
  }

  if (Array.isArray(result)) {
    result.forEach(item => {
      mapCount(
        item.regionCode || item.code || item.region || item.name || item.title,
        item.count != null ? item.count : item.num
      )
    })
  } else if (typeof result === 'object') {
    Object.keys(result).forEach(key => {
      const val = result[key]
      if (val && typeof val === 'object') {
        mapCount(
          val.regionCode || val.code || key,
          val.count != null ? val.count : val.num
        )
      } else {
        mapCount(key, val)
      }
    })
  }
  return base
}
